/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackException } from 'helpers/errorTracker';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useClaimOrganizationMutation, useUpdateNetworkKindMutation } from 'services/organizationsV2';
import { useAddNetworkAdminMutation } from 'services/networkAdmins';
import strings from 'strings';
import { setActiveNetwork, updateNetwork, setNetworks } from '../../../actions/networksActions';
import { trackEvent } from '../../../helpers/analytics';
import { setUser } from '../../../actions/userActions';

import { networksSelector, userSelector } from '../../../redux/selectors';
import { useLazyGetUserQuery, useUpdateUserMutation } from '../../../services/user';
import { useShareContactsMutation, useUpdateUserGoalsMutation } from '../../../services/userV2';
import { checkExtensionAvailability } from '../../../services/chromeExtension';
import { legalDocumentsVersion } from '../../../settings/legalDocumentsVersion';
import { useCompanyNetwork } from './useCompanyNetwork';
import { useGoogleSync } from '../../../hooks/useGoogleSync';
import { useHasGoogleSyncFeature } from '../../../hooks/useHasGoogleSyncFeature';
import { useTierGating } from '../../../hooks/useTierGating';
import { deriveTier, hasAccessToContacts as hasAccessToContactsFn } from '../../../services/tierGating';

const stepToPath = (step, slug) => `/networks/${slug}/welcome/onboarding/${step}`;
const postOnboardingPath = (slug, hasContacts) => (hasContacts ? `/networks/${slug}/home` : `/networks/${slug}`);

export const useOnboarding = () => {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const { path, params } = useRouteMatch();
  const { networks } = useSelector(networksSelector);
  const user = useSelector(userSelector);
  const localNetwork = useMemo(
    () => Object.values(networks).find(({ slug }) => slug === params?.networkSlug),
    [networks, params?.networkSlug],
  );
  const {
    network: companyNetwork,
    isLoading: isLoadingCompanyNetwork,
    isFetching: isFetchingCompanyNetwork,
    company,
  } = useCompanyNetwork();

  const network = localNetwork || companyNetwork;
  const { hasAccessToContacts, hasCapability } = useTierGating({ network });
  const canImport = hasCapability('contacts_import');
  const canInviteAllies = hasCapability('invite_allies');

  // Check integration status for skip/flow logic
  const { googleSync } = useGoogleSync();
  const hasGoogleSyncFeature = useHasGoogleSyncFeature({ network });
  const isLinkedinConnected = !!user.linkedinSync?.syncStatus && user.linkedinSync?.syncStatus !== 'not_connected';
  const isGoogleConnected = !!googleSync?.syncStatus && googleSync.syncStatus !== 'not_connected';
  // All integrations done = LinkedIn connected AND (Google feature disabled OR Google connected)
  const allIntegrationsDone = isLinkedinConnected && (!hasGoogleSyncFeature || isGoogleConnected);
  // Any integration connected = LinkedIn OR (Google feature enabled AND connected)
  const anyIntegrationConnected = isLinkedinConnected || (hasGoogleSyncFeature && isGoogleConnected);
  const isSharingContacts = !!network?.shareContacts;

  // Share contacts step: shown when user has integration connected but isn't sharing yet
  const shouldShowShareContactsStep = canImport && anyIntegrationConnected && !isSharingContacts;

  const showNetworkKindStep = network?.kindSetupByCurrentUser || network?.kindSetupAt === null || !network?.kind;
  const isOnIntegrationsStep = params?.step === 'integrations';

  const steps = useMemo(() => {
    const result = [];

    // Profile: shown when user hasn't completed onboarding yet, or during company onboarding
    if (!user?.onboardingCompleted || params?.employerSlug) {
      result.push('profile');
    }

    // Non-customer networks only see the profile step
    if (!hasAccessToContacts) {
      return result;
    }

    // Network kind: shown when kind is not set or onboarding is still in progress (skip for company onboarding)
    if (!params?.employerSlug && (showNetworkKindStep || !user?.onboardingCompleted)) {
      result.push('network-kind');
    }

    // Goals: always shown for customers
    result.push('goals');

    // Integrations (LinkedIn/Google): gated by contacts_import capability, skipped if all integrations are done
    if (canImport && (!allIntegrationsDone || isOnIntegrationsStep)) {
      result.push('integrations');
    }

    // Share contacts: gated by contacts_import, shown only when user has integrations connected but isn't sharing yet
    if (shouldShowShareContactsStep) {
      result.push('share-contacts');
    }

    // Team: gated by invite_allies capability
    if (canInviteAllies) {
      result.push('team');
    }

    return result;
  }, [
    user?.onboardingCompleted,
    params?.employerSlug,
    hasAccessToContacts,
    showNetworkKindStep,
    allIntegrationsDone,
    shouldShowShareContactsStep,
    isOnIntegrationsStep,
    canImport,
    canInviteAllies,
  ]);

  const [updateUser, { isLoading: isLoadingUpdateUser }] = useUpdateUserMutation();
  const [shareContacts] = useShareContactsMutation();
  const [add, { isLoading: isLoadingAddAdmin }] = useAddNetworkAdminMutation();

  const dispatch = useDispatch();
  const history = useHistory();
  const [users, setUsers] = useState([
    { id: 1, name: '', email: '' },
    { id: 2, name: '', email: '' },
    { id: 3, name: '', email: '' },
  ]);

  const [networkKind, setNetworkKind] = useState(network?.kind);
  const [goals, setGoals] = useState(new Set(network?.userGoals ?? []));
  const [updateUserGoals, { isLoading: isLoadingUpdateUserGoals }] = useUpdateUserGoalsMutation();
  const [updateKind, { isLoading: isLoadingUpdateNetworkKind }] = useUpdateNetworkKindMutation();
  const [claimOrganization, { isLoading: isClaimingOrganization }] = useClaimOrganizationMutation();
  const [getUser, { isLoading: isLoadingGetUser }] = useLazyGetUserQuery();

  useEffect(() => {
    checkExtensionAvailability()
      .then((availability) => {
        setIsExtensionInstalled(availability);
      })
      .catch(() => {
        setIsExtensionInstalled(false);
      });
  }, []);

  useEffect(() => {
    if (Array.isArray(network?.userGoals)) {
      setGoals((prevGoals) => {
        // Compare by value to avoid redundant updates when Redux creates new array references
        const prevSorted = Array.from(prevGoals).sort().join(',');
        const newSorted = [...network.userGoals].sort().join(',');
        return prevSorted === newSorted ? prevGoals : new Set(network.userGoals);
      });
    }
  }, [network?.userGoals]);

  useEffect(() => {
    if (network?.kind !== undefined) {
      setNetworkKind(network.kind);
    }
  }, [network?.kind]);

  const addAdmin = useCallback(
    async (usersToAdd) => Promise.allSettled(usersToAdd.map((admin) => add({ networkId: network?.id, admin }))),
    [add, network],
  );

  const autoShareContacts = useCallback(async () => {
    if (!network?.id || network?.shareContacts) return;
    shareContacts({ collectionId: network.id, share: true })
      .unwrap()
      .then(() => {
        trackEvent('contacts:contacts_sharing:enabled', {
          collectionId: network.id,
          page: 'onboarding',
        });
      });
  }, [network?.id, network?.shareContacts, shareContacts]);

  const getNextStep = useCallback(
    async (step, { slug: defaultSlug } = {}) => {
      const stepEventNames = {
        profile: 'account',
        'network-kind': 'network_type',
        goals: 'goals',
        integrations: 'integrations',
        'share-contacts': 'share_contacts',
        team: 'invite_team',
      };
      const slug = defaultSlug || network?.slug || companyNetwork?.slug;
      const stepEventName = stepEventNames[step];
      if (stepEventName) {
        trackEvent('admin_onboarding:finish_step', { step_name: stepEventName });
      }

      if (step === 'goals' && canImport && allIntegrationsDone && !steps.includes('integrations')) {
        trackEvent('admin_onboarding:finish_step', { step_name: 'integrations' });
      }

      const currentIndex = steps.indexOf(step);
      if (currentIndex < 0) {
        if (steps.length > 0) {
          return stepToPath(steps[0], slug);
        }
        trackEvent('admin_onboarding:complete');
        return postOnboardingPath(slug, hasAccessToContacts);
      }

      const nextIndex = currentIndex + 1;
      if (nextIndex >= steps.length) {
        trackEvent('admin_onboarding:complete');
        return postOnboardingPath(slug, hasAccessToContacts);
      }

      return stepToPath(steps[nextIndex], slug);
    },
    [network?.slug, companyNetwork?.slug, steps, allIntegrationsDone, canImport, hasAccessToContacts],
  );

  const handleNextStep = useCallback(
    async (props, options = {}) => {
      const nextStep = await getNextStep(params.step, props);
      const networkUpdateAlreadyHandled = options.skipCompletion === true;
      const slug = network?.slug || companyNetwork?.slug;
      const isCompletingOnboarding = slug && nextStep === postOnboardingPath(slug, true);

      if (isCompletingOnboarding && hasAccessToContacts && !user?.onboardingCompleted) {
        try {
          const response = await updateUser({ onboardingCompleted: true });
          if (!response.error) {
            dispatch(
              setUser({
                ...user,
                onboardingCompleted: true,
              }),
            );
            if (network?.id && !networkUpdateAlreadyHandled) {
              dispatch(
                updateNetwork({
                  id: network.id,
                  userGoalsAnswered: true,
                }),
              );
            }
          }
        } catch (error) {
          trackException(error);
        }
      }
      history.push(nextStep);
    },
    [
      getNextStep,
      history,
      params,
      hasAccessToContacts,
      user,
      updateUser,
      dispatch,
      network?.id,
      network?.slug,
      companyNetwork?.slug,
    ],
  );

  const updateUserProfile = useCallback(
    async ({ avatarUrl, firstName, lastName, password }) => {
      const options = {
        avatarUrl,
        firstName,
        lastName,
        password,
        currentPassword: user.password,
        passwordConfirmation: password,
        termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
        privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
        onboardingCompleted: !hasAccessToContacts,
      };

      const response = await updateUser(options).catch((error) => {
        trackException(error);

        throw error;
      });

      // if it's company onboarding, claim the organization
      if (params.employerSlug) {
        const { error: claimError, data: claimResponse } = await claimOrganization({
          collectionId: companyNetwork?.id,
          organizationId: company?.id,
        });
        if (claimError) {
          toast(
            claimError?.data?.code === 'company_claim.domain_not_valid'
              ? claimError?.data?.error
              : strings.genericError,
            {
              icon: 'error',
              id: 'claim-organization',
            },
          );
          return response;
        }

        const { data, error } = await getUser();
        if (error) {
          toast(strings.genericError, {
            icon: 'error',
            id: 'get-user',
          });
          return response;
        }
        dispatch(setUser(data));
        dispatch(setNetworks(data));
        const networkData = data.managingCollections.find(
          (collection) => collection.slug === claimResponse?.companyCollection?.slug,
        );
        dispatch(setActiveNetwork(networkData.id));

        // Check if claimed network is a customer - non-customers go directly to network root
        const claimedNetworkTier = deriveTier(networkData);
        const claimedNetworkHasAccess = hasAccessToContactsFn(claimedNetworkTier);
        if (!claimedNetworkHasAccess) {
          // Persist onboardingCompleted to the server - the earlier updateUser at line 276
          // may have sent false if the pre-claim network was a customer
          await updateUser({ onboardingCompleted: true });
          dispatch(
            setUser({
              ...data,
              onboardingCompleted: true,
            }),
          );
          history.push(`/networks/${networkData.slug}`);
          return response;
        }

        history.push(`/networks/${networkData.slug}/welcome/onboarding/goals`);

        return response;
      }

      if (!hasAccessToContacts) {
        dispatch(
          setUser({
            avatarUrl,
            firstName,
            lastName,
            onboardingCompleted: true,
          }),
        );
        dispatch(
          updateNetwork({
            id: network.id,
            kind: networkKind,
          }),
        );
        history.push(`/networks/${network?.slug}`);

        return response;
      }

      handleNextStep();

      return response;
    },
    [
      user.password,
      hasAccessToContacts,
      network?.slug,
      network?.id,
      updateUser,
      params.employerSlug,
      handleNextStep,
      companyNetwork?.id,
      dispatch,
      networkKind,
      history,
      claimOrganization,
      company?.id,
      getUser,
    ],
  );

  const updateNetworkKind = async (kind) => {
    setNetworkKind(kind);
    // Reset goals when network kind changes (company vs ecosystem have different goal options)
    setGoals(new Set());
    const response = await updateKind({ networkId: network?.id, kind });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-network-kind',
      });
      return;
    }

    dispatch(
      updateNetwork({
        id: network?.id,
        kind,
        kindSetupByCurrentUser: true,
        userGoals: [],
      }),
    );

    handleNextStep(kind);
  };

  const updateGoals = async (goalsToUpdate) => {
    setGoals(goalsToUpdate);

    const response = await updateUserGoals({ networkId: network?.id, goals: Array.from(goalsToUpdate) });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-user-goals',
      });
      return;
    }

    // Update Redux so goals persist if component remounts (e.g., when navigating back)
    dispatch(
      updateNetwork({
        id: network?.id,
        userGoals: Array.from(goalsToUpdate),
      }),
    );

    handleNextStep(goalsToUpdate);
  };

  const updateTeam = async (props) => {
    setUsers((prev) => {
      const map = new Map();
      props.usersToAdd.forEach((item) => {
        map.set(item.id, item);
      });
      return prev.map((item) => {
        if (map.has(item.id)) {
          return map.get(item.id);
        }
        return item;
      });
    });

    if (!props.skip && props.usersToAdd.length > 0) {
      const results = await addAdmin(props.usersToAdd);
      const hasFailure = results.some((r) => r.value.error);
      if (hasFailure) {
        toast(strings.genericError, {
          icon: 'error',
          id: 'update-team',
        });

        return;
      }
    }
    const { avatarUrl, firstName, lastName } = user;

    if (!user.onboardingCompleted) {
      await updateUser({
        onboardingCompleted: true,
      });
    }

    dispatch(
      setUser({
        avatarUrl,
        firstName,
        lastName,
        kind: networkKind,
        goals: Array.from(goals),
        onboardingCompleted: true,
      }),
    );

    dispatch(
      updateNetwork({
        id: network?.id,
        kind: networkKind,
        userGoals: Array.from(goals),
        userGoalsAnswered: true,
      }),
    );

    handleNextStep(props, { skipCompletion: true });
  };

  const handleGoogleSyncComplete = useCallback(async () => {
    trackEvent('admin_onboarding:google_sync_complete', {
      collection_id: network?.id,
    });
    await autoShareContacts();
  }, [network?.id, autoShareContacts]);

  return {
    isLoadingCompanyNetwork: isLoadingCompanyNetwork || isFetchingCompanyNetwork,
    activeNetwork: network,
    networks,
    path,
    steps,
    isLoadingUpdateUser: isLoadingUpdateUser || isClaimingOrganization || isLoadingGetUser,
    updateUserProfile,
    handleNextStep,
    users,
    networkKind,
    goals,
    setUsers,
    setNetworkKind,
    setGoals,
    updateUserGoals,
    isLoadingUpdateUserGoals,
    updateNetworkKind,
    isLoadingUpdateNetworkKind,
    isLoadingAddAdmin: isLoadingAddAdmin || isLoadingUpdateUser,
    user,
    params,
    updateTeam,
    updateGoals,
    showNetworkKindStep,
    skipSignUp:
      user?.onboardingCompleted && (user.hasPassword || user.oauthProviders.length > 0) && !params.employerSlug,
    profileMode:
      (!user?.hasPassword || user?.forcePasswordUpdate) && !user?.oauthProviders?.length ? 'email' : 'password',
    isExtensionInstalled,
    companyNetwork,
    company,
    allIntegrationsDone,
    anyIntegrationConnected,
    shouldShowShareContactsStep,
    handleGoogleSyncComplete,
  };
};
